import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  BarChart3,
  FileText,
  Plus,
  Search,
  ArrowRight,
  Clock,
  AlertTriangle,
  CheckCircle,
  CreditCard,
  RefreshCw,
  Infinity,
  Zap,
  TrendingUp,
  Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useAuth } from '@/hooks/useAuth';
import { useAnalytics } from '@/hooks/useAnalytics';
import { supabase } from '@/utils/supabase';
import { stripeService } from '@/services/stripe-service';

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUserCredits } = useAuth();
  const analytics = useAnalytics();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [analyses, setAnalyses] = useState<any[]>([]);
  const [completedAnalyses, setCompletedAnalyses] = useState<any[]>([]);

  console.log('🔍 DEBUG Dashboard: User object in Dashboard:', user); // Add this line
  console.log('🔍 DEBUG Dashboard: user?.subscription_status:', user?.subscription_status); // Add this line
  // No recent analyses for a new user
  const recentAnalyses: any[] = [];

  useEffect(() => {
    // Track page view
    analytics.trackEvent({
      category: 'page_view',
      action: 'view',
      label: 'Dashboard'
    });
    
    // Load user data
    loadAnalysesData();
  }, []);
  
  const loadAnalysesData = async () => {
    try {
      setLoading(true);
      
      // Get user's analyses
      const { data: analysesData, error: analysesError } = await supabase
        .from('dpe_data')
        .select('id, status, created_at')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });
      
      if (analysesError) {
        console.error('Error fetching analyses:', analysesError);
      } else {
        setAnalyses(analysesData || []);
        setCompletedAnalyses((analysesData || []).filter(a => a.status === 'completed'));
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefreshCredits = async () => {
    try {
      setRefreshing(true);
      await refreshUserCredits();
      await loadAnalysesData();
    } catch (error) {
      console.error('Error refreshing credits:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Utiliser directement les données du hook useAuth
  const credits = user?.credits || 0;
  const hasActiveSubscription = user?.subscription_status === 'active' || 
                                user?.subscription_status === 'trialing';
  
  // ✅ NOUVEAU : Debug pour identifier les problèmes d'accès
  React.useEffect(() => {
    console.log('🔍 DEBUG Dashboard - User access status:', {
      userId: user?.id,
      credits: user?.credits,
      subscription_status: user?.subscription_status,
      subscription_id: user?.subscription_id,
      hasActiveSubscription,
      hasCredits: credits > 0,
      canAccessPremium: hasActiveSubscription || credits > 0
    });
  }, [user, credits, hasActiveSubscription]);

  return (
    <div>
      <Helmet>
        <title>Tableau de bord - DPEWizard</title>
        <meta name="description" content="Tableau de bord DPEWizard - Gérez vos analyses DPE, consultez vos statistiques et accédez à toutes vos fonctionnalités." />
      </Helmet>
      
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Tableau de bord
          </h1>
          <p className="text-gray-600">
            Bienvenue, {user?.first_name || user?.email?.split('@')[0]}. Voici un aperçu de vos analyses DPE.
          </p>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefreshCredits}
          disabled={refreshing}
          icon={<RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />}
        >
          {refreshing ? 'Actualisation...' : 'Actualiser'}
        </Button>
      </div>

      {/* Stats Cards - Réorganisées en carré harmonieux */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total analyses</p>
                <h3 className="text-2xl font-bold text-gray-900 mt-1">{analyses.length}</h3>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-1 overflow-hidden">
                <div className="h-full bg-blue-600" style={{ width: `${analyses.length > 0 ? (completedAnalyses.length / analyses.length) * 100 : 0}%` }} />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {completedAnalyses.length} analyses complétées
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Crédits disponibles</p>
                <h3 className="text-2xl font-bold text-gray-900 mt-1">
                  {hasActiveSubscription ? (
                    <div className="flex items-center">
                      <Infinity className="w-6 h-6 mr-1" />
                      <span>Illimité</span>
                    </div>
                  ) : (
                    <span>{credits}</span>
                  )}
                </h3>
              </div>
              <div 
                className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center cursor-pointer hover:bg-green-200 transition-colors"
                onClick={() => navigate('/analysis/list')}
              >
                <CreditCard className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4">
              {hasActiveSubscription ? (
                <Badge variant="success">Abonnement Premium actif</Badge>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/pricing')}
                >
                  Acheter des crédits
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Analyses terminées</p>
                <h3 className="text-2xl font-bold text-gray-900 mt-1">{completedAnalyses.length}</h3>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-xs text-gray-500">
                {analyses.length > 0 ? Math.round((completedAnalyses.length / analyses.length) * 100) : 0}% de réussite
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Analyses en cours</p>
                <h3 className="text-2xl font-bold text-gray-900 mt-1">
                  {analyses.filter(a => ['draft', 'analyzing', 'extracting'].includes(a.status)).length}
                </h3>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-xs text-gray-500">
                En cours de traitement
              </p>
            </div>
          </CardContent>
        </Card>

      </div>
      
      {/* Bloc upsell abonnement — visible uniquement pour les non-abonnés */}
      {!hasActiveSubscription && (
        <div className="mb-8 rounded-2xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Infinity className="w-4 h-4 text-blue-500" />
                <span className="text-blue-600 text-xs font-semibold uppercase tracking-wide">
                  Abonnement illimité
                </span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                Analysez tous vos biens sans compter
              </h3>
              <p className="text-gray-600 text-sm mb-4 max-w-md">
                Un crédit à l'unité coûte 9,90€. Dès 2 analyses par mois,
                l'abonnement à <strong>19,90€/mois</strong> vous revient moins cher — avec des analyses illimitées.
              </p>
              <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Analyses DPE illimitées</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Scénarios de travaux inclus</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Rapports PDF détaillés</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Sans engagement</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2 shrink-0 text-center">
              <div>
                <span className="text-3xl font-bold text-gray-900">19,90€</span>
                <span className="text-gray-500 text-sm">/mois</span>
              </div>
              <div className="text-xs text-gray-400 mb-1">ou 190€/an · 2 mois offerts</div>
              <Button
                variant="primary"
                size="md"
                onClick={() => {
                  analytics.trackEvent({
                    category: 'conversion',
                    action: 'click_upsell_dashboard',
                    label: 'dashboard_upgrade_banner'
                  });
                  navigate('/pricing');
                }}
              >
                Passer à l'abonnement
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mb-8 mt-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Actions rapides
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card hover className="cursor-pointer" onClick={() => navigate('/analysis/new')}>
            <CardContent className="p-6 flex items-center">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <Plus className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Nouvelle analyse</h3>
                <p className="text-sm text-gray-600">Analyser un nouveau DPE</p>
              </div>
            </CardContent>
          </Card>

          <Card hover className="cursor-pointer" onClick={() => navigate('/analysis/list')}>
            <CardContent className="p-6 flex items-center">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Mes analyses</h3>
                <p className="text-sm text-gray-600">Voir toutes mes analyses</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;